import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import UpgradeModal from "@/components/app/UpgradeModal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowUpRight, LayoutTemplate, Archive, ArchiveRestore, Trash2, Download, Sparkles, FileSearch2, Columns3, GanttChartSquare, MessageCircle, BookOpen, FileText, FilePlus, Upload, FileDown, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import PmpTemplatePicker from "@/components/app/PmpTemplatePicker";

const SOW_BLANK = { project_id: "", client_name: "", project_name: "", scope: "", deliverables: "", timeline: "", payment_terms: "", total_value: "", currency: "GBP", notes: "" };

const Workspace = () => {
  const [projects, setProjects] = useState([]);
  const [deals, setDeals] = useState([]);
  const [entities, setEntities] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [form, setForm] = useState({
    name: "", description: "", methodology: "agile", status: "planning", deal_id: "", target_end_date: "", entity_id: "",
    budget_hours: "", budget_cash: "", phases_text: "", member_ids: [], pmp_document_titles: [],
  });
  const [busy, setBusy] = useState(false);
  const [lifecycleFilter, setLifecycleFilter] = useState("active");
  const [teamMembers, setTeamMembers] = useState([]);

  // Auto-open the create-project dialog when arrived via "New Project" (?new=1)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // SOW Documents
  const [sowDocs, setSowDocs] = useState([]);
  const [sowDocsOpen, setSowDocsOpen] = useState(false);
  const [showCreateSow, setShowCreateSow] = useState(false);
  const [sowDocForm, setSowDocForm] = useState(SOW_BLANK);
  const [sowDocCreating, setSowDocCreating] = useState(false);
  const [showUploadSow, setShowUploadSow] = useState(false);
  const [sowUploadForm, setSowUploadForm] = useState({ name: "", description: "", project_id: "" });
  const [sowFile, setSowFile] = useState(null);
  const [sowUploading, setSowUploading] = useState(false);

  const [sowOpen, setSowOpen] = useState(false);
  const [sowBusy, setSowBusy] = useState(false);
  const [sowExtracting, setSowExtracting] = useState(false);
  const [sowForm, setSowForm] = useState({
    name: "", contract_value: "", target_margin_percentage: "",
    phases_text: "", sow_text: "", methodology: "hybrid",
  });

  const load = async () => {
    const [projRes, dealRes, entRes] = await Promise.all([
      api.get(`/projects?lifecycle=${lifecycleFilter}`),
      api.get("/deals").catch(() => ({ data: [] })),
      api.get("/entities").catch(() => ({ data: [] })),
    ]);
    setProjects(projRes.data);
    setDeals(dealRes.data);
    setEntities(entRes.data || []);
  };

  useEffect(() => {
    api.get("/team/members").then(({ data }) => setTeamMembers(data || [])).catch(() => {});
  }, []);

  const loadSowDocs = async () => {
    try {
      const { data } = await api.get("/documents?category=sow");
      setSowDocs(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lifecycleFilter]);
  useEffect(() => { loadSowDocs(); }, []);

  const createSowDoc = async () => {
    if (!sowDocForm.client_name || !sowDocForm.project_name || !sowDocForm.scope) {
      toast.error("Client name, project name and scope are required"); return;
    }
    setSowDocCreating(true);
    try {
      await api.post("/documents/sow", {
        ...sowDocForm,
        project_id: sowDocForm.project_id || null,
        total_value: sowDocForm.total_value ? parseFloat(sowDocForm.total_value) : null,
      });
      toast.success("SOW created");
      setShowCreateSow(false);
      setSowDocForm(SOW_BLANK);
      loadSowDocs();
    } catch { toast.error("Failed to create SOW"); } finally { setSowDocCreating(false); }
  };

  const uploadSowDoc = async () => {
    if (!sowFile) { toast.error("Select a file"); return; }
    if (!sowUploadForm.name) { toast.error("Enter a document name"); return; }
    setSowUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", sowFile);
      fd.append("name", sowUploadForm.name);
      fd.append("description", sowUploadForm.description);
      fd.append("category", "sow");
      if (sowUploadForm.project_id) fd.append("project_id", sowUploadForm.project_id);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SOW uploaded");
      setShowUploadSow(false);
      setSowUploadForm({ name: "", description: "", project_id: "" });
      setSowFile(null);
      loadSowDocs();
    } catch { toast.error("Upload failed"); } finally { setSowUploading(false); }
  };

  const deleteSowDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      setSowDocs(prev => prev.filter(d => d.doc_id !== doc.doc_id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const downloadSowDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const exportSowPdf = (doc) => {
    const sow = doc.sow_data;
    if (!sow) { downloadSowDoc(doc); return; }
    const sym = { GBP: "£", USD: "$", EUR: "€" }[sow.currency] || (sow.currency || "£");
    const valueStr = sow.total_value ? `${sym}${parseFloat(sow.total_value).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "TBC";
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const sec = (t, v) => v ? `<h2>${esc(t)}</h2><p>${esc(v)}</p>` : "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>SOW – ${esc(sow.project_name)}</title>
<style>body{font-family:'Times New Roman',serif;max-width:760px;margin:48px auto;color:#111;line-height:1.7;font-size:13px}h1{text-align:center;font-size:22px;text-transform:uppercase;letter-spacing:2px;margin-bottom:2px}.sub{text-align:center;color:#555;margin-bottom:36px;font-size:12px}table{width:100%;border-collapse:collapse;margin-bottom:20px}td{padding:5px 10px}td:first-child{font-weight:bold;width:140px}h2{font-size:12px;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #333;padding-bottom:3px;margin-top:24px;margin-bottom:8px}p{white-space:pre-wrap;margin:0}.total{font-size:16px;font-weight:bold;text-align:right;margin-top:32px;padding-top:10px;border-top:2px solid #111}.btn{display:block;margin:24px auto;padding:8px 24px;background:#002FA7;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer}@media print{@page{margin:1in}.btn{display:none}}</style></head>
<body><h1>Statement of Work</h1><div class="sub">Generated ${new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</div>
<table><tr><td>Project:</td><td>${esc(sow.project_name)}</td></tr><tr><td>Client:</td><td>${esc(sow.client_name)}</td></tr><tr><td>Currency:</td><td>${esc(sow.currency||"GBP")}</td></tr></table>
${sec("Scope of Work",sow.scope)}${sec("Deliverables",sow.deliverables)}${sec("Timeline",sow.timeline)}${sec("Payment Terms",sow.payment_terms)}${sec("Additional Notes",sow.notes)}
<div class="total">Total Value: ${valueStr}</div>
<button class="btn" onclick="window.print()">Print / Save as PDF</button></body></html>`;
    const w = window.open("", "_blank", "width=900,height=720");
    if (!w) { toast.error("Allow popups to export PDF"); return; }
    w.document.write(html); w.document.close();
  };

  const setLifecycle = async (e, projectId, state) => {
    e.preventDefault(); e.stopPropagation();
    const verb = state === "active" ? "restored" : state === "archived" ? "archived" : "moved to trash";
    if (state !== "active" && !window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} this project?`)) return;
    try {
      await api.put(`/projects/${projectId}/lifecycle`, { state });
      toast.success(`Project ${verb}`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const exportProject = async (e, projectId, projectName) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const res = await api.get(`/projects/${projectId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${projectName.replace(/\s+/g, "_")}_Export.zip`; a.click();
      URL.revokeObjectURL(url); toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const create = async () => {
    if (!form.name.trim()) return;
    const phases = form.phases_text.split("\n").map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      const { data } = await api.post("/projects", {
        name: form.name, description: form.description, methodology: form.methodology, status: form.status,
        deal_id: form.deal_id || null, target_end_date: form.target_end_date || null, entity_id: form.entity_id || null,
        budget_hours: form.budget_hours ? parseFloat(form.budget_hours) : null,
        budget_cash: form.budget_cash ? parseFloat(form.budget_cash) : null,
        phases: phases.length ? phases : null,
        member_ids: form.member_ids,
        pmp_document_titles: form.pmp_document_titles.length ? form.pmp_document_titles : null,
      });
      const extras = [];
      if (data.phases_created) extras.push(`${data.phases_created} phase${data.phases_created === 1 ? "" : "s"}`);
      if (data.documents_generated) extras.push(`${data.documents_generated} PMBOK document${data.documents_generated === 1 ? "" : "s"}`);
      toast.success(extras.length ? `Project created with ${extras.join(" and ")}` : "Project created");
      if (data.margin_risk_flagged) toast.warning(`Margin risk flagged on '${data.name}' — review before kicking off.`);
      setForm({ name: "", description: "", methodology: "agile", status: "planning", deal_id: "", target_end_date: "", entity_id: "", budget_hours: "", budget_cash: "", phases_text: "", member_ids: [], pmp_document_titles: [] });
      setOpen(false); load();
    } catch (err) {
      if (err?.response?.status === 402) {
        setOpen(false);
        setUpgradeReason(err.response.data?.detail || "Upgrade your plan to create more projects.");
        setUpgradeOpen(true);
      } else {
        toast.error(err?.response?.data?.detail || "Failed to create project");
      }
    } finally { setBusy(false); }
  };

  const toggleMember = (userId) => {
    setForm((f) => ({
      ...f,
      member_ids: f.member_ids.includes(userId) ? f.member_ids.filter((id) => id !== userId) : [...f.member_ids, userId],
    }));
  };

  const extractPhasesAI = async () => {
    if (!sowForm.sow_text || sowForm.sow_text.trim().length < 30) { toast.error("Paste at least a paragraph of SOW text first"); return; }
    setSowExtracting(true);
    try {
      const res = await api.post("/ai/extract-sow-phases", { sow_text: sowForm.sow_text });
      setSowForm((f) => ({ ...f, phases_text: (res.data.phases || []).join("\n") }));
      toast.success(`Extracted ${res.data.phases.length} phase${res.data.phases.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Extraction failed"); } finally { setSowExtracting(false); }
  };

  const ingestSOW = async () => {
    if (!sowForm.name.trim()) return toast.error("Project name required");
    const cv = parseFloat(sowForm.contract_value), tm = parseFloat(sowForm.target_margin_percentage);
    if (!(cv >= 0)) return toast.error("Contract value must be ≥ 0");
    if (!(tm >= 0 && tm <= 100)) return toast.error("Target margin must be 0-100");
    const phases = sowForm.phases_text.split("\n").map((s) => s.trim()).filter(Boolean);
    if (phases.length === 0) return toast.error("At least one phase required");
    setSowBusy(true);
    try {
      const res = await api.post("/projects/ingest-sow", { name: sowForm.name, contract_value: cv, target_margin_percentage: tm, phases, methodology: sowForm.methodology });
      if (res.data.margin_risk_flagged) toast.warning(`Margin risk flagged on '${res.data.project.name}' — review before kicking off.`);
      else toast.success(`Ingested ${res.data.tasks.length} phase${res.data.tasks.length === 1 ? "" : "s"} into '${res.data.project.name}'`);
      setSowForm({ name: "", contract_value: "", target_margin_percentage: "", phases_text: "", sow_text: "", methodology: "hybrid" });
      setSowOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "SOW ingestion failed"); } finally { setSowBusy(false); }
  };

  const HEALTH = {
    green: "bg-[#00F5D4]",
    amber: "bg-[#F59E0B]",
    red: "bg-[#FF007A]",
  };

  return (
    <DarkPage>
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />
      <PageHeader
        eyebrow="Project workspace"
        title="Projects"
        description="Create and manage every project. One source of truth."
        actions={
          <div className="flex gap-2">
            <Link to="/templates">
              <Button variant="outline" className="rounded-none border-[#374151] bg-transparent text-[#D1D5DB] hover:bg-[#1F2937] hover:text-white" data-testid="workspace-from-template-btn">
                <LayoutTemplate className="w-4 h-4 mr-1" /> From template
              </Button>
            </Link>

            <Dialog open={sowOpen} onOpenChange={setSowOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none border-[#374151] bg-transparent text-[#D1D5DB] hover:bg-[#1F2937] hover:text-white" data-testid="ingest-sow-btn">
                  <FileSearch2 className="w-4 h-4 mr-1" /> Ingest SOW
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl rounded-none border-[#1F2937] bg-[#111827] text-white">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight text-white">Ingest Statement of Work</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Project name</Label>
                      <Input className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="sow-name-input" value={sowForm.name} onChange={(e) => setSowForm({ ...sowForm, name: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Methodology</Label>
                      <Select value={sowForm.methodology} onValueChange={(v) => setSowForm({ ...sowForm, methodology: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="sow-methodology-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
                          <SelectItem value="agile">Agile</SelectItem>
                          <SelectItem value="waterfall">Waterfall</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Contract value (£)</Label>
                      <Input type="number" min="0" step="0.01" className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="sow-contract-input" placeholder="e.g. 85000" value={sowForm.contract_value} onChange={(e) => setSowForm({ ...sowForm, contract_value: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Target margin (%)</Label>
                      <Input type="number" min="0" max="100" step="0.1" className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="sow-margin-input" placeholder="e.g. 25" value={sowForm.target_margin_percentage} onChange={(e) => setSowForm({ ...sowForm, target_margin_percentage: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Raw SOW text (optional — for AI extraction)</Label>
                      <Button type="button" variant="outline" size="sm" className="rounded-none h-7 border-[#374151] bg-transparent text-[#9CA3AF] hover:border-[#00F5D4] hover:text-[#00F5D4] text-xs" onClick={extractPhasesAI} disabled={sowExtracting || !sowForm.sow_text} data-testid="sow-extract-ai-btn">
                        <Sparkles className="w-3 h-3 mr-1" /> {sowExtracting ? "Extracting…" : "Extract phases with AI"}
                      </Button>
                    </div>
                    <Textarea rows={4} className="rounded-none mt-1 font-mono text-xs bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563]" placeholder="Paste the SOW or contract preamble. Gemini will extract the delivery phases for you." data-testid="sow-text-input" value={sowForm.sow_text} onChange={(e) => setSowForm({ ...sowForm, sow_text: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Delivery phases (one per line)</Label>
                    <Textarea rows={6} className="rounded-none mt-1 font-mono text-xs bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563]" placeholder={"Discovery & Mobilisation\nDesign Sprint\nBuild Phase 1\nUAT & Cutover\nHypercare"} data-testid="sow-phases-input" value={sowForm.phases_text} onChange={(e) => setSowForm({ ...sowForm, phases_text: e.target.value })} />
                    <div className="mt-1 text-[11px] text-[#6B7280]">Each phase is scaffolded as a top-level task with a 40h baseline.</div>
                  </div>
                  <div className="border-l-2 border-[#F59E0B] bg-[#F59E0B]/8 px-3 py-2 text-[11px] text-[#F59E0B]">
                    Sanity check: target margin &gt; 50% on a contract &lt; £5,000 will be auto-flagged as <strong>High Margin Risk</strong>.
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={ingestSOW} disabled={sowBusy} className="rounded-none bg-[#00F5D4] text-[#030712] hover:bg-[#00e5c4] font-bold shadow-glow-teal" data-testid="confirm-ingest-sow">
                    {sowBusy ? "Ingesting…" : "Ingest & scaffold phases"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#00F5D4] text-[#030712] hover:bg-[#00e5c4] font-bold shadow-glow-teal" data-testid="new-project-btn">
                  <Plus className="w-4 h-4 mr-1" /> New project
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl rounded-none border-[#1F2937] bg-[#111827] text-white max-h-[88vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight text-white">Create project</DialogTitle>
                  <p className="text-xs text-[#6B7280]">Everything here is manual — no AI required. Fill in what's useful and skip the rest.</p>
                </DialogHeader>
                <div className="space-y-5">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Name</Label>
                    <Input className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Description</Label>
                    <Textarea className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Methodology</Label>
                      <Select value={form.methodology} onValueChange={(v) => setForm({ ...form, methodology: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-methodology-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
                          <SelectItem value="agile">Agile</SelectItem>
                          <SelectItem value="waterfall">Waterfall</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
                          <SelectItem value="planning">Planning</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="on_hold">On hold</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Linked deal (CRM)</Label>
                      <Select value={form.deal_id || "none"} onValueChange={(v) => setForm({ ...form, deal_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-deal-select"><SelectValue placeholder="No deal" /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
                          <SelectItem value="none">No deal</SelectItem>
                          {deals.map((d) => <SelectItem key={d.deal_id} value={d.deal_id}>{d.name} {d.client ? `(${d.client})` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Target end date</Label>
                      <Input type="date" className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-target-end-input" value={form.target_end_date} onChange={(e) => setForm({ ...form, target_end_date: e.target.value })} />
                    </div>
                  </div>
                  {entities.length > 0 && (
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Business entity</Label>
                      <Select value={form.entity_id || "none"} onValueChange={(v) => setForm({ ...form, entity_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-entity-select"><SelectValue placeholder="No entity" /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
                          <SelectItem value="none">No entity</SelectItem>
                          {entities.map((e) => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}{e.code ? ` (${e.code})` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="border-t border-[#1F2937] pt-4">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#00F5D4] mb-3">Budget (optional)</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Budget hours</Label>
                        <Input type="number" min="0" step="0.5" className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-budget-hours-input" placeholder="e.g. 320" value={form.budget_hours} onChange={(e) => setForm({ ...form, budget_hours: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Budget (£)</Label>
                        <Input type="number" min="0" step="0.01" className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="project-budget-cash-input" placeholder="e.g. 45000" value={form.budget_cash} onChange={(e) => setForm({ ...form, budget_cash: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  {teamMembers.length > 0 && (
                    <div className="border-t border-[#1F2937] pt-4">
                      <div className="text-[11px] uppercase tracking-[0.25em] text-[#00F5D4] mb-3">Team (optional)</div>
                      <div className="flex flex-wrap gap-2" data-testid="project-team-picker">
                        {teamMembers.map((m) => {
                          const active = form.member_ids.includes(m.user_id);
                          return (
                            <button
                              type="button"
                              key={m.user_id}
                              onClick={() => toggleMember(m.user_id)}
                              data-testid={`project-team-member-${m.user_id}`}
                              className={`px-3 py-1.5 text-xs border transition-colors ${active ? "border-[#00F5D4] bg-[#00F5D4]/10 text-[#00F5D4]" : "border-[#374151] text-[#9CA3AF] hover:border-[#00F5D4]/50"}`}
                            >
                              {m.name || m.email}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-[#1F2937] pt-4">
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-[#00F5D4]">Delivery phases (optional, one per line)</Label>
                    <Textarea rows={4} className="rounded-none mt-2 font-mono text-xs bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563]" placeholder={"Discovery\nDesign\nBuild\nUAT & Launch"} data-testid="project-phases-input" value={form.phases_text} onChange={(e) => setForm({ ...form, phases_text: e.target.value })} />
                    <div className="mt-1 text-[11px] text-[#6B7280]">Each phase becomes a top-level task with a 40h baseline — no template or AI needed.</div>
                  </div>

                  <div className="border-t border-[#1F2937] pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#00F5D4]">PMBOK documents (optional)</Label>
                      {form.pmp_document_titles.length > 0 && (
                        <span className="text-[11px] text-[#6B7280]">{form.pmp_document_titles.length} selected</span>
                      )}
                    </div>
                    <PmpTemplatePicker
                      selected={form.pmp_document_titles}
                      onChange={(titles) => setForm({ ...form, pmp_document_titles: titles })}
                    />
                    <div className="mt-2 text-[11px] text-[#6B7280]">Pick any of the 41 PMBOK templates to generate as blank, editable placeholders under this project's Documents.</div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} disabled={busy} className="rounded-none bg-[#00F5D4] text-[#030712] hover:bg-[#00e5c4] font-bold shadow-glow-teal" data-testid="confirm-create-project">
                    {busy ? "Creating…" : "Create project"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8">
        {/* Lifecycle filter tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-[#1F2937]" data-testid="lifecycle-tabs">
          {[
            { key: "active", label: "Active" },
            { key: "archived", label: "Archived" },
            { key: "soft_deleted", label: "Trash" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setLifecycleFilter(t.key)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] border-b-2 transition ${
                lifecycleFilter === t.key
                  ? "border-[#00F5D4] text-white font-semibold"
                  : "border-transparent text-[#6B7280] hover:text-white"
              }`}
              data-testid={`lifecycle-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {projects.length === 0 ? (
          <div className="border border-dashed border-[#1F2937] p-16 text-center">
            <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">
              {lifecycleFilter === "active" ? "Nothing here yet" : lifecycleFilter === "archived" ? "Nothing archived" : "Trash is empty"}
            </div>
            <h3 className="font-display font-black text-3xl tracking-tight mt-2 text-white">
              {lifecycleFilter === "active" ? "Create your first project" : "Switch to Active"}
            </h3>
            {lifecycleFilter === "active" && (
              <>
                <p className="text-[#9CA3AF] mt-2">Spin up a workspace, then let the AI assistant draft your charter.</p>
                <Button onClick={() => setOpen(true)} className="rounded-none mt-6 bg-[#00F5D4] text-[#030712] hover:bg-[#00e5c4] font-bold shadow-glow-teal" data-testid="empty-create-btn">
                  <Plus className="w-4 h-4 mr-1" /> New project
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2937] border border-[#1F2937]">
            {projects.map((p) => {
              const progress = Math.min(100, Math.max(0, p.progress || 0));
              const healthColor = { green: "#00F5D4", amber: "#F59E0B", red: "#FF007A" }[p.health] || "#374151";
              return (
              <div key={p.project_id} className="bg-[#111827] hover:bg-[#151e2e] transition-colors group relative flex flex-col" data-testid={`project-card-${p.project_id}`}>
                <Link to={`/workspace/${p.project_id}`} className="flex-1 p-6 block">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-sm" style={{ background: healthColor }} />
                      <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{p.methodology}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{p.status?.replace("_", " ")}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl tracking-tight mt-5 text-white">{p.name}</h3>
                  <p className="text-sm text-[#9CA3AF] mt-1.5 line-clamp-2 min-h-[40px]">{p.description || "No description"}</p>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[10px] text-[#6B7280] mb-1">
                      <span className="uppercase tracking-[0.2em]">Progress</span>
                      <span className="font-mono" style={{ color: healthColor }}>{progress}%</span>
                    </div>
                    <div className="h-1 bg-[#1F2937] w-full rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: healthColor }} />
                    </div>
                  </div>

                  {/* Meta row */}
                  <div className="mt-3 flex items-center gap-3 text-[10px] text-[#6B7280] flex-wrap">
                    {p.contract_value ? (
                      <span className="font-mono">£{Number(p.contract_value).toLocaleString()}</span>
                    ) : null}
                    {p.target_end_date && (
                      <span className="font-mono">▸ {new Date(p.target_end_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })}</span>
                    )}
                  </div>
                </Link>

                {/* Quick-nav footer */}
                <div className="border-t border-[#1F2937] px-3 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {[
                      { to: `/workspace/${p.project_id}/board`, icon: Columns3,          label: "Board" },
                      { to: `/workspace/${p.project_id}/gantt`, icon: GanttChartSquare,  label: "Gantt" },
                      { to: `/workspace/${p.project_id}/chat`,  icon: MessageCircle,     label: "Chat"  },
                      { to: `/workspace/${p.project_id}/wiki`,  icon: BookOpen,          label: "Wiki"  },
                    ].map(({ to, icon: Icon, label }) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 text-[#4B5563] hover:text-[#00F5D4] hover:bg-[#00F5D4]/8 rounded transition-colors"
                        title={label}
                        data-testid={`quicknav-${label.toLowerCase()}-${p.project_id}`}
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.7} />
                      </Link>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition" data-testid={`actions-${p.project_id}`}>
                  <button onClick={(e) => exportProject(e, p.project_id, p.name)} className="p-1.5 bg-[#1F2937] border border-[#374151] hover:border-[#00F5D4] hover:text-[#00F5D4] text-[#9CA3AF] rounded-sm transition-colors" title="Export project as ZIP" data-testid={`export-${p.project_id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {lifecycleFilter === "active" && (
                    <button onClick={(e) => setLifecycle(e, p.project_id, "archived")} className="p-1.5 bg-[#1F2937] border border-[#374151] hover:border-[#F59E0B] hover:text-[#F59E0B] text-[#9CA3AF] rounded-sm transition-colors" title="Archive project" data-testid={`archive-${p.project_id}`}>
                      <Archive className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {lifecycleFilter !== "active" && (
                    <button onClick={(e) => setLifecycle(e, p.project_id, "active")} className="p-1.5 bg-[#1F2937] border border-[#374151] hover:border-[#00F5D4] hover:text-[#00F5D4] text-[#9CA3AF] rounded-sm transition-colors" title="Restore" data-testid={`restore-${p.project_id}`}>
                      <ArchiveRestore className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {lifecycleFilter !== "soft_deleted" && (
                    <button onClick={(e) => setLifecycle(e, p.project_id, "soft_deleted")} className="p-1.5 hover:text-[#FF007A] text-[#4B5563] rounded transition-colors" title="Move to trash" data-testid={`trash-${p.project_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* ── SOW Documents ─────────────────────────────────────────────── */}
        <div className="mt-10 border border-[#1F2937]">
          <button
            onClick={() => setSowDocsOpen(v => !v)}
            className="w-full h-12 flex items-center justify-between px-5 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white hover:bg-[#111827] transition-colors"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-[#00F5D4]" />
              Statements of Work{sowDocs.length > 0 ? ` · ${sowDocs.length}` : ""}
            </div>
            {sowDocsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {sowDocsOpen && (
            <div className="border-t border-[#1F2937] p-5">
              <div className="flex gap-2 mb-5">
                <button onClick={() => setShowCreateSow(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#00F5D4] text-[#030712] font-semibold rounded-sm hover:bg-[#00e5c4]">
                  <FilePlus className="w-3.5 h-3.5" /> Create SOW
                </button>
                <button onClick={() => setShowUploadSow(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#374151] text-slate-400 hover:text-white rounded-sm">
                  <Upload className="w-3.5 h-3.5" /> Upload
                </button>
              </div>
              {sowDocs.length === 0 ? (
                <p className="text-sm text-[#6B7280]">No SOW documents yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sowDocs.map(doc => {
                    const proj = projects.find(p => p.project_id === doc.project_id);
                    return (
                      <div key={doc.doc_id} className="bg-[#111827] border border-[#1F2937] rounded-lg p-4 flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded bg-[#1F2937] border border-[#374151] flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-[#00F5D4]" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-white text-sm font-medium truncate">{doc.name}</div>
                            {proj && <div className="text-xs text-[#6B7280] mt-0.5">{proj.name}</div>}
                            <div className="text-xs text-[#4B5563] mt-0.5">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ""}</div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-auto">
                          {doc.sow_data ? (
                            <button onClick={() => exportSowPdf(doc)} className="flex-1 flex items-center justify-center gap-1 text-xs text-[#00F5D4] hover:text-white bg-[#1F2937] border border-[#00F5D4]/30 rounded-sm py-1.5">
                              <FileDown className="w-3.5 h-3.5" /> Export PDF
                            </button>
                          ) : (
                            <button onClick={() => downloadSowDoc(doc)} className="flex-1 flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-white bg-[#1F2937] border border-[#374151] rounded-sm py-1.5">
                              <Download className="w-3.5 h-3.5" /> Download
                            </button>
                          )}
                          <button onClick={() => deleteSowDoc(doc)} className="p-1.5 text-[#4B5563] hover:text-rose-400 bg-[#1F2937] border border-[#374151] rounded-sm">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create SOW modal */}
      {showCreateSow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#111827] border border-[#1F2937] rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold text-lg">Create Statement of Work</h2>
              <button onClick={() => setShowCreateSow(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[["Client Name *","client_name","Acme Corp"],["Project Name *","project_name","Website Redesign"]].map(([lbl,key,ph]) => (
                <div key={key}>
                  <label className="text-xs text-[#9CA3AF] mb-1 block">{lbl}</label>
                  <input value={sowDocForm[key]} onChange={e => setSowDocForm(v => ({...v,[key]:e.target.value}))} placeholder={ph} className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-xs text-[#9CA3AF] mb-1 block">Link to Project</label>
                <select value={sowDocForm.project_id} onChange={e => setSowDocForm(v => ({...v,project_id:e.target.value}))} className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">None</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-xs text-[#9CA3AF] mb-1 block">Total Value</label>
                  <input type="number" value={sowDocForm.total_value} onChange={e => setSowDocForm(v => ({...v,total_value:e.target.value}))} placeholder="10000" className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="w-24">
                  <label className="text-xs text-[#9CA3AF] mb-1 block">Currency</label>
                  <select value={sowDocForm.currency} onChange={e => setSowDocForm(v => ({...v,currency:e.target.value}))} className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm">
                    {["GBP","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              {[["Scope of Work *","scope",3,"Describe the work…"],["Deliverables","deliverables",3,"List key deliverables…"],["Timeline","timeline",2,"e.g. 12 weeks starting 1 Aug"],["Payment Terms","payment_terms",2,"e.g. 50% upfront"],["Additional Notes","notes",2,"Any additional terms…"]].map(([lbl,key,rows,ph]) => (
                <div key={key} className="md:col-span-2">
                  <label className="text-xs text-[#9CA3AF] mb-1 block">{lbl}</label>
                  <textarea value={sowDocForm[key]} onChange={e => setSowDocForm(v => ({...v,[key]:e.target.value}))} rows={rows} placeholder={ph} className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm resize-none" />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={createSowDoc} disabled={sowDocCreating} className="flex-1 bg-[#00F5D4] text-[#030712] font-semibold py-2 rounded-md text-sm hover:bg-[#00e5c4] disabled:opacity-50">
                {sowDocCreating ? "Creating…" : "Create SOW"}
              </button>
              <button onClick={() => setShowCreateSow(false)} className="px-4 py-2 border border-[#374151] text-slate-400 rounded-md text-sm hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload SOW modal */}
      {showUploadSow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-[#111827] border border-[#1F2937] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Upload SOW</h2>
              <button onClick={() => setShowUploadSow(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#9CA3AF] mb-1 block">Document name *</label>
                <input value={sowUploadForm.name} onChange={e => setSowUploadForm(v => ({...v,name:e.target.value}))} placeholder="e.g. Project Alpha SOW v2" className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#9CA3AF] mb-1 block">Project (optional)</label>
                <select value={sowUploadForm.project_id} onChange={e => setSowUploadForm(v => ({...v,project_id:e.target.value}))} className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">None</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#9CA3AF] mb-1 block">File *</label>
                <input type="file" onChange={e => setSowFile(e.target.files[0])} className="w-full bg-[#030712] border border-[#374151] text-white rounded-md px-3 py-2 text-sm file:mr-3 file:bg-[#1F2937] file:border-0 file:text-xs file:text-white file:rounded file:px-2 file:py-1" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={uploadSowDoc} disabled={sowUploading} className="flex-1 bg-[#00F5D4] text-[#030712] font-semibold py-2 rounded-md text-sm hover:bg-[#00e5c4] disabled:opacity-50">
                {sowUploading ? "Uploading…" : "Upload"}
              </button>
              <button onClick={() => setShowUploadSow(false)} className="px-4 py-2 border border-[#374151] text-slate-400 rounded-md text-sm hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </DarkPage>
  );
};

export default Workspace;
